<template>
	<JBox>
		<JBox class="s-p-4" v-if="isLoading">
			<!-- Loading message -->
			<JFlex class="s-bg-white s-p-4 s-rounded s-justify-between s-items-center">
				<div class="s-mr-2 s-text-gray-900 s-text-md">{{ $t("templates.loadingFileMessage") }}</div>
				<JSpinner variant="default" type="dots" :delay="0" />
			</JFlex>
			<!-- Errors -->
			<div class="s-p-4">
				<li v-for="(item, i) in errors" :key="i" class="s-text-red-600 s-font-medium s-cursor-pointer">
					{{ item }}
				</li>
			</div>
		</JBox>
		<canvas id="pdfViewer" class="viewer " v-show="!isLoading" />
	</JBox>
</template>

<script>
const pdfjs = require("pdfjs-dist/es5/build/pdf.js")
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry"
import { bearerHeaders } from "@/modules/auth/cookieMode.js"

export default {
	props: {
		fileKey: {
			type: String
		},
		scale: {
			type: Number,
			default: () => 1
		},
		page: {
			type: Number
		},
		showPreview: {
			type: Boolean
		},
		previewResult: {
			type: [String, Uint8Array]
		},
		isLoading: {
			type: Boolean
		}
	},

	data() {
		return {
			pdf: null,
			numPages: null,

			height: undefined,
			width: undefined,
			pageNumPending: null,

			fetchedPreviewBytes: null,

			errors: null
		}
	},

	methods: {
		async fetchPdfData() {
			if (!this.fileKey) return

			let src = null

			if (this.showPreview && this.previewResult) {
				src = this.previewResult
			} else
				src = {
					url: `${window.envConfig?.COVERGO_FILE_SYSTEM}/${encodeURIComponent(this.fileKey)}`,
					httpHeaders: bearerHeaders(this.$store?.state?.auth?.token)
				}

			await pdfjs
				.getDocument(src)
				.promise.then(pdf => {
					this.pdf = pdf
					this.numPages = pdf.numPages
					this.renderPage(this.page)
				})
				.then(() => {
					this.isPreviewLoading = false
				})
		},
		async renderPage(pageNumber) {
			await this.pdf.getPage(pageNumber).then(async page => {
				let viewport = page.getViewport({ scale: this.scale })
				let pdfViewer = document.getElementById("pdfViewer")

				pdfViewer.height = viewport.height
				pdfViewer.width = viewport.width

				this.height = pdfViewer.height
				this.width = pdfViewer.width

				page
					.render({
						canvasContext: pdfViewer.getContext("2d"),
						viewport
					})
					.promise.then(() => {
						if (this.pageNumPending !== null) {
							// New page rendering is pending
							this.renderPage(this.pageNumPending)
							this.pageNumPending = null
						}
					})
					.catch(error => {
						this.$emit("viewport:errorMessage", error.message)
						if (error.name === "RenderingCancelledException") {
							this.renderPage(pageNumber)
							return
						}
					})
			})
		}
	},

	computed: {
		computedViewport() {
			return {
				height: this.height,
				width: this.width
			}
		},
		computedProps() {
			const { fileKey, showPreview, previewResult } = this
			return { fileKey, showPreview, previewResult }
		}
	},

	watch: {
		computedProps: {
			handler() {
				this.fetchPdfData()
			},
			immediate: true
		},
		computedViewport: {
			handler() {
				this.$emit("viewport:updated", this.computedViewport)
			},
			immediate: true,
			deep: true
		},
		scale: {
			handler() {
				this.renderPage(this.page)
			}
		},
		page: {
			handler() {
				this.renderPage(this.page)
			}
		},
		numPages: {
			handler() {
				this.$emit("numberOfPages", this.numPages)
			},
			immediate: true
		}
	},

	created() {
		pdfjs.disableFontFace = false
		pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker
	}
}
</script>
